'use client';

import { maybe } from '@/utils/null-utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { memo, useContext, useEffect, useState, type FC } from 'react';
import { RouterEventDispatcherContext } from './internal/RouterEventDispatcherContext';

/**
 * A component which hooks into pathname and search params changes to dispatch router events.
 *
 * This is primarily used to shim the `routeChangeStart`
 * and `routeChangeComplete` events into App Router.
 *
 * **NOTE**: This does not work with shallow routing on the Pages Router.
 */
export const RouterEvents: FC = memo(function RouterEvents() {
  const pathname = usePathname();
  // In Pages router, searchParams will be null on the first render of a static page.
  const searchParams = maybe(useSearchParams());

  const routerEventDispatcher = useContext(RouterEventDispatcherContext);

  const currentPathQuery = `${pathname}?${searchParams?.toString() ?? ''}`;

  const [prevPathQuery, setPrevPathQuery] = useState(currentPathQuery);

  useEffect(() => {
    if (prevPathQuery !== currentPathQuery) {
      setPrevPathQuery(currentPathQuery);
      routerEventDispatcher('routeChangeStart', [currentPathQuery]);
    } else {
      routerEventDispatcher('routeChangeComplete', [currentPathQuery]);
    }
  }, [currentPathQuery, prevPathQuery, routerEventDispatcher]);

  return null;
});
